import React, { useMemo } from 'react';

interface YouTubeEmbedProps {
  videoId?: string;
  url?: string;
  title?: string;
  autoplay?: boolean;
  controls?: boolean;
  start?: number;
  modestBranding?: boolean;
  rel?: 0 | 1;
  className?: string;
}

const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({
  videoId,
  url,
  title = 'YouTube video player',
  autoplay = false,
  controls = true,
  start = 0,
  modestBranding = true,
  rel = 0,
  className = ''
}) => {
  const id = useMemo(() => {
    if (videoId) return videoId.trim();
    if (!url) return '';

    try {
      const u = new URL(url);
      if (u.hostname.includes('youtu.be')) {
        return u.pathname.slice(1);
      }
      if (u.searchParams.get('v')) {
        return u.searchParams.get('v') || '';
      }
      const parts = u.pathname.split('/');
      const embedIndex = parts.findIndex(p => p === 'embed');
      if (embedIndex !== -1 && parts[embedIndex + 1]) {
        return parts[embedIndex + 1];
      }
    } catch {
      // ignore invalid URL
    }
    return '';
  }, [videoId, url]);

  if (!id) {
    return (
      <div
        role="alert"
        style={{
          padding: '12px',
          borderRadius: 8,
          background: '#fff3cd',
          border: '1px solid #ffeeba',
          color: '#856404',
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif'
        }}
      >
        Unable to determine a YouTube video ID. Provide a valid{' '}
        <code>videoId</code> or <code>url</code>.
      </div>
    );
  }

  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    controls: controls ? '1' : '0',
    start: String(start || 0),
    modestbranding: modestBranding ? '1' : '0',
    rel: String(rel),
    playsinline: '1'
  });

  const src = `https://www.youtube.com/embed/${id}?${params.toString()}`;

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%' // 16:9 ratio
      }}
    >
      <iframe
        title={title}
        src={src}
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 0,
          borderRadius: 12
        }}
      />
    </div>
  );
};

export default YouTubeEmbed;
